/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Building2, Home, MapPin, Users, Briefcase, TrendingUp, DollarSign, Plus,
  Search, X, Loader2, Trash2, Eye, Calendar, CheckCircle2, AlertCircle,
  Clock, Award, Wallet, Settings as SettingsIcon, Image as ImageIcon, BarChart3,
  Target, FileText, UserCheck,
} from "lucide-react";
import { LeadsView } from "./realestate/LeadsView";
import { TeamLeadsView } from "./realestate/TeamLeadsView";
import { DailyReportsView } from "./realestate/DailyReportsView";
import { AnalyticsView } from "./realestate/AnalyticsView";

const PROPERTY_TYPES = [
  { v: "land", lbl: "Land" }, { v: "apartment", lbl: "Apartment" },
  { v: "duplex", lbl: "Duplex" }, { v: "flat", lbl: "Flat" },
  { v: "bungalow", lbl: "Bungalow" }, { v: "commercial", lbl: "Commercial" },
  { v: "office", lbl: "Office Space" }, { v: "warehouse", lbl: "Warehouse" },
  { v: "estate_plot", lbl: "Estate Plot" }, { v: "rental", lbl: "Rental" },
  { v: "short_let", lbl: "Short-let" },
];
const STATUS_OPTIONS = [
  { v: "draft", lbl: "Draft", color: "bg-zinc-100 text-zinc-700" },
  { v: "available", lbl: "Available", color: "bg-emerald-100 text-emerald-800" },
  { v: "reserved", lbl: "Reserved", color: "bg-amber-100 text-amber-800" },
  { v: "sold", lbl: "Sold", color: "bg-blue-100 text-blue-800" },
  { v: "pending", lbl: "Pending", color: "bg-violet-100 text-violet-800" },
  { v: "under_review", lbl: "Under Review", color: "bg-orange-100 text-orange-800" },
  { v: "archived", lbl: "Archived", color: "bg-black/10 text-black/60" },
];
const TABS = [
  { key: "dashboard", lbl: "Dashboard", icon: BarChart3 },
  { key: "analytics", lbl: "Analytics", icon: Target },
  { key: "properties", lbl: "Properties", icon: Home },
  { key: "sales", lbl: "Sales", icon: TrendingUp },
  { key: "leads", lbl: "Leads / CRM", icon: Users },
  { key: "marketers", lbl: "Marketers", icon: Users },
  { key: "team-leads", lbl: "Team Leads", icon: UserCheck },
  { key: "agencies", lbl: "Agencies", icon: Briefcase },
  { key: "commissions", lbl: "Commissions", icon: Wallet },
  { key: "payouts", lbl: "Payouts", icon: DollarSign },
  { key: "reports", lbl: "Daily Reports", icon: FileText },
  { key: "settings", lbl: "Settings", icon: SettingsIcon },
];

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function RealEstatePage() {
  const { subdomain } = useParams();
  const [tab, setTab] = useState("dashboard");
  const [planLocked, setPlanLocked] = useState(false);
  useEffect(() => { api.get("/t/re/dashboard").catch((e) => { if (e?.response?.status === 403) setPlanLocked(true); }); }, []);

  if (planLocked) return (
    <div className="p-8"><div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="re-plan-locked">
      <Building2 size={32} className="mx-auto text-amber-700 mb-3" />
      <div className="font-display font-extrabold text-xl text-amber-900">Real Estate module is not in your plan</div>
      <Link to={`/t/${subdomain}/billing`} className="inline-block mt-4 px-5 py-2.5 rounded-xl bg-[#1FA868] text-white font-bold text-sm">View plans</Link>
    </div></div>
  );

  return (
    <div className="p-6 lg:p-8" data-testid="re-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-600/30">
            <Building2 size={26} />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-black">Real Estate</h1>
            <p className="text-sm text-black/55 mt-1">Properties · Marketers · Agencies · Commissions</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-6 border-b border-black/8 overflow-x-auto" data-testid="re-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`re-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap ${active ? "border-blue-600 text-blue-700" : "border-transparent text-black/55 hover:text-black"}`}>
              <Icon size={15} /> {t.lbl}
            </button>
          );
        })}
      </div>
      {tab === "dashboard" && <DashboardView />}
      {tab === "analytics" && <AnalyticsView />}
      {tab === "properties" && <PropertiesView />}
      {tab === "sales" && <SalesView />}
      {tab === "leads" && <LeadsView />}
      {tab === "marketers" && <MarketersView />}
      {tab === "team-leads" && <TeamLeadsView />}
      {tab === "agencies" && <AgenciesView />}
      {tab === "commissions" && <CommissionsView />}
      {tab === "payouts" && <PayoutsView />}
      {tab === "reports" && <DailyReportsView />}
      {tab === "settings" && <SettingsView />}
    </div>
  );
}

function DashboardView() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/t/re/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Spinner />;
  const k = d.kpis;
  return (
    <div data-testid="re-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI title="Total Properties" value={k.total_properties} icon={Home} testid="re-kpi-properties" />
        <KPI title="Available" value={k.available} icon={CheckCircle2} accent="bg-emerald-50 text-emerald-700" testid="re-kpi-available" />
        <KPI title="Sold" value={k.sold} icon={Award} accent="bg-blue-50 text-blue-700" testid="re-kpi-sold" />
        <KPI title="Revenue" value={ngn(k.revenue)} icon={TrendingUp} accent="bg-violet-50 text-violet-700" testid="re-kpi-revenue" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPI title="Paid Commissions" value={ngn(k.paid_commissions)} icon={Wallet} accent="bg-emerald-50 text-emerald-700" />
        <KPI title="Pending Commissions" value={ngn(k.pending_commissions)} icon={Clock} accent="bg-amber-50 text-amber-700" />
        <KPI title="Marketers" value={k.marketers_total} icon={Users} />
        <KPI title="Open Inquiries" value={k.open_inquiries} icon={AlertCircle} accent="bg-sky-50 text-sky-700" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Status breakdown">
          <div className="space-y-2">
            {STATUS_OPTIONS.map((s) => {
              const v = d.status_counts?.[s.v] || 0;
              const max = Math.max(1, ...Object.values(d.status_counts || {}));
              return (
                <div key={s.v}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-bold text-black/75">{s.lbl}</span>
                    <span className="font-bold tabular-nums">{v}</span>
                  </div>
                  <div className="h-2 bg-black/5 rounded-full"><div className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full" style={{ width: `${(v / max) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card title="Top marketers">
          {d.top_marketers?.length ? d.top_marketers.map((m, i) => (
            <div key={m.id} className="flex items-center gap-3 py-2.5 border-b border-black/5 last:border-0">
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">{i + 1}</div>
              <div className="flex-1 min-w-0"><div className="font-bold text-sm text-black">{m.name}</div><div className="text-xs text-black/55">{m.sales} sales</div></div>
              <div className="font-bold text-sm text-emerald-700">{ngn(m.revenue)}</div>
            </div>
          )) : <div className="text-sm text-black/45 text-center py-4">No sales yet</div>}
        </Card>
      </div>
    </div>
  );
}

function PropertiesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusF !== "all") params.set("status", statusF);
    if (q.trim()) params.set("q", q.trim());
    try { setItems((await api.get(`/t/re/properties?${params}`)).data || []); } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusF]);
  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/t/re/properties/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  return (
    <div data-testid="re-properties-view">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by title, code, city…" className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-black/15 text-sm" data-testid="re-prop-search" />
        </div>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="px-3 py-2.5 rounded-lg border border-black/15 text-sm font-semibold" data-testid="re-prop-status-filter">
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.lbl}</option>)}
        </select>
        <button onClick={() => setEditing({ _new: true })} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="re-prop-new"><Plus size={14} /> New Property</button>
      </div>
      {loading ? <Spinner /> : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/8 p-12 text-center" data-testid="re-prop-empty">
          <Home size={32} className="mx-auto text-black/25 mb-3" />
          <div className="font-bold text-black/70">No properties yet</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => {
            const s = STATUS_OPTIONS.find((x) => x.v === p.status) || STATUS_OPTIONS[0];
            return (
              <div key={p.id} className="bg-white rounded-2xl border border-black/8 overflow-hidden hover:shadow-md transition" data-testid={`re-prop-card-${p.id}`}>
                {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-40 object-cover" /> : <div className="w-full h-40 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center"><ImageIcon size={32} className="text-blue-300" /></div>}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-display font-extrabold text-base text-black line-clamp-1">{p.title}</div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.color}`}>{s.lbl}</span>
                  </div>
                  <div className="text-[10px] text-black/45 mb-2 font-mono">{p.code}</div>
                  <div className="flex items-center gap-1 text-xs text-black/55 mb-2"><MapPin size={11} /> {p.city || "—"}, {p.state || "—"}</div>
                  <div className="font-display font-extrabold text-xl text-[#1FA868]">{ngn(p.price)}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-black/60">
                    {p.bedrooms > 0 && <span>{p.bedrooms} bed</span>}
                    {p.bathrooms > 0 && <span>{p.bathrooms} bath</span>}
                    {p.size_sqm > 0 && <span>{p.size_sqm} sqm</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/5">
                    <button onClick={() => setEditing(p)} className="flex-1 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold" data-testid={`re-prop-edit-${p.id}`}>Edit</button>
                    <button onClick={() => remove(p.id)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" data-testid={`re-prop-delete-${p.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && <PropertyEditor prop={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PropertyEditor({ prop, onClose, onSaved }) {
  const isNew = prop._new;
  const [f, setF] = useState({
    title: prop.title || "", type: prop.type || "apartment", price: prop.price || 0,
    commission_pct: prop.commission_pct ?? 5, team_lead_commission_pct: prop.team_lead_commission_pct ?? 1,
    state: prop.state || "", city: prop.city || "", address: prop.address || "",
    bedrooms: prop.bedrooms || 0, bathrooms: prop.bathrooms || 0, size_sqm: prop.size_sqm || 0,
    description: prop.description || "", status: prop.status || "draft", is_featured: !!prop.is_featured,
    images: prop.images || [], amenities: prop.amenities || [],
    installment_enabled: !!prop.installment_enabled, installment_months: prop.installment_months || 12,
  });
  const [saving, setSaving] = useState(false);
  const uploadImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/t/products/upload", fd); setF((s) => ({ ...s, images: [...s.images, r.data.url] })); toast.success("Uploaded"); }
    catch (e) { toast.error("Upload failed"); }
  };
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      if (isNew) await api.post("/t/re/properties", f);
      else await api.patch(`/t/re/properties/${prop.id}`, f);
      toast.success(isNew ? "Created" : "Updated"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isNew ? "New Property" : "Edit Property"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Title *" full><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="re-prop-title" /></Field>
        <Field lbl="Type"><select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="re-prop-type">{PROPERTY_TYPES.map((t) => <option key={t.v} value={t.v}>{t.lbl}</option>)}</select></Field>
        <Field lbl="Status"><select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="re-prop-status">{STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.lbl}</option>)}</select></Field>
        <Field lbl="Price (₦) *"><Input type="number" value={f.price} onChange={(v) => setF({ ...f, price: parseFloat(v) || 0 })} testid="re-prop-price" /></Field>
        <Field lbl="State"><Input value={f.state} onChange={(v) => setF({ ...f, state: v })} /></Field>
        <Field lbl="City"><Input value={f.city} onChange={(v) => setF({ ...f, city: v })} /></Field>
        <Field lbl="Bedrooms"><Input type="number" value={f.bedrooms} onChange={(v) => setF({ ...f, bedrooms: parseInt(v) || 0 })} /></Field>
        <Field lbl="Bathrooms"><Input type="number" value={f.bathrooms} onChange={(v) => setF({ ...f, bathrooms: parseInt(v) || 0 })} /></Field>
        <Field lbl="Size (sqm)"><Input type="number" value={f.size_sqm} onChange={(v) => setF({ ...f, size_sqm: parseFloat(v) || 0 })} /></Field>
        <Field lbl="Commission %"><Input type="number" value={f.commission_pct} onChange={(v) => setF({ ...f, commission_pct: parseFloat(v) || 0 })} testid="re-prop-comm" /></Field>
        <Field lbl="Team Lead %"><Input type="number" value={f.team_lead_commission_pct} onChange={(v) => setF({ ...f, team_lead_commission_pct: parseFloat(v) || 0 })} /></Field>
        <Field lbl="Address" full><Input value={f.address} onChange={(v) => setF({ ...f, address: v })} /></Field>
        <Field lbl="Description" full><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
        <Field lbl="Images" full>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {f.images.map((u, i) => (
              <div key={i} className="relative">
                <img src={u} alt="" className="w-full h-20 object-cover rounded" />
                <button onClick={() => setF({ ...f, images: f.images.filter((_, idx) => idx !== i) })} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center"><X size={11} /></button>
              </div>
            ))}
            <label className="border-2 border-dashed border-black/15 rounded h-20 flex items-center justify-center text-black/45 text-xs cursor-pointer hover:border-blue-500 hover:text-blue-600">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
              + Upload
            </label>
          </div>
        </Field>
        <Field lbl="" full>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={f.is_featured} onChange={(e) => setF({ ...f, is_featured: e.target.checked })} className="accent-[#1FA868]" /> Featured property
          </label>
        </Field>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="re-prop-save" />
    </Modal>
  );
}

function SalesView() {
  const [sales, setSales] = useState([]);
  const [props, setProps] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  useEffect(() => {
    api.get("/t/re/sales").then((r) => setSales(r.data || [])).catch(() => {});
    api.get("/t/re/properties?status=available").then((r) => setProps(r.data || []));
    api.get("/t/re/marketers").then((r) => setMarketers(r.data || []));
  }, [showNew]);
  return (
    <div data-testid="re-sales-view">
      <div className="flex items-center justify-end mb-4">
        <button onClick={() => setShowNew(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="re-sale-new"><Plus size={14} /> New Sale</button>
      </div>
      {sales.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/8 p-12 text-center"><TrendingUp size={32} className="mx-auto text-black/25 mb-3" /><div className="font-bold text-black/70">No sales yet</div></div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide text-black/55 font-bold">
              <tr><Th>Code</Th><Th>Property</Th><Th>Customer</Th><Th right>Price</Th><Th right>Paid</Th><Th>Stage</Th></tr>
            </thead>
            <tbody>
              {sales.map((s) => (
                <tr key={s.id} className="border-t border-black/5" data-testid={`re-sale-row-${s.id}`}>
                  <Td className="font-mono text-xs">{s.code}</Td>
                  <Td>{s.property_title}</Td>
                  <Td>{s.customer_name}</Td>
                  <Td right className="font-bold">{ngn(s.sale_price)}</Td>
                  <Td right>{ngn(s.amount_paid)}</Td>
                  <Td><span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">{s.stage}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <SaleEditor properties={props} marketers={marketers} onClose={() => setShowNew(false)} onSaved={() => setShowNew(false)} />}
    </div>
  );
}

function SaleEditor({ properties, marketers, onClose, onSaved }) {
  const [f, setF] = useState({
    property_id: properties[0]?.id || "", customer_name: "", customer_email: "", customer_phone: "",
    sale_price: properties[0]?.price || 0, marketer_id: "", team_lead_id: "", referral_code: "",
    payment_plan: "full", initial_payment: 0, installments_total: 12,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.property_id || !f.customer_name.trim()) return toast.error("Property and customer name required");
    setSaving(true);
    try { await api.post("/t/re/sales", f); toast.success("Sale recorded"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Record Sale" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Property *" full>
          <select value={f.property_id} onChange={(e) => { const p = properties.find((x) => x.id === e.target.value); setF({ ...f, property_id: e.target.value, sale_price: p?.price || 0 }); }} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="sale-property">
            <option value="">Select property…</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.title} ({ngn(p.price)})</option>)}
          </select>
        </Field>
        <Field lbl="Customer name *" full><Input value={f.customer_name} onChange={(v) => setF({ ...f, customer_name: v })} testid="sale-cname" /></Field>
        <Field lbl="Customer email"><Input value={f.customer_email} onChange={(v) => setF({ ...f, customer_email: v })} /></Field>
        <Field lbl="Customer phone"><Input value={f.customer_phone} onChange={(v) => setF({ ...f, customer_phone: v })} /></Field>
        <Field lbl="Marketer (closer)"><select value={f.marketer_id} onChange={(e) => setF({ ...f, marketer_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="sale-marketer"><option value="">None</option>{marketers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
        <Field lbl="Team Lead"><select value={f.team_lead_id} onChange={(e) => setF({ ...f, team_lead_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm"><option value="">None</option>{marketers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
        <Field lbl="Sale price (₦)"><Input type="number" value={f.sale_price} onChange={(v) => setF({ ...f, sale_price: parseFloat(v) || 0 })} testid="sale-price" /></Field>
        <Field lbl="Referral code"><Input value={f.referral_code} onChange={(v) => setF({ ...f, referral_code: v })} placeholder="Optional" /></Field>
        <Field lbl="Payment plan">
          <select value={f.payment_plan} onChange={(e) => setF({ ...f, payment_plan: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">
            <option value="full">Full payment</option><option value="installment">Installments</option>
          </select>
        </Field>
        {f.payment_plan === "installment" && (
          <>
            <Field lbl="Initial payment"><Input type="number" value={f.initial_payment} onChange={(v) => setF({ ...f, initial_payment: parseFloat(v) || 0 })} /></Field>
            <Field lbl="Installments"><Input type="number" value={f.installments_total} onChange={(v) => setF({ ...f, installments_total: parseInt(v) || 0 })} /></Field>
          </>
        )}
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="sale-save" />
    </Modal>
  );
}

function MarketersView() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPromote, setShowPromote] = useState(false);
  const load = () => api.get("/t/re/marketers").then((r) => setItems(r.data || []));
  useEffect(() => { load(); api.get("/t/hrm/employees").then((r) => setEmployees((r.data || []).map((e) => ({ ...e, name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email })))).catch(() => {}); }, []);
  const remove = async (id) => { if (!window.confirm("Remove marketer?")) return; await api.delete(`/t/re/marketers/${id}`); load(); };
  return (
    <div data-testid="re-marketers-view">
      <div className="flex items-center justify-end mb-4"><button onClick={() => setShowPromote(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="re-promote-btn"><Plus size={14} /> Promote Employee</button></div>
      {items.length === 0 ? <div className="bg-white rounded-2xl border border-black/8 p-12 text-center"><Users size={32} className="mx-auto text-black/25 mb-3" /><div className="font-bold text-black/70">No marketers yet</div></div> : (
        <div className="bg-white rounded-2xl border border-black/8 divide-y divide-black/5">
          {items.map((m) => (
            <div key={m.id} className="p-4 flex items-center gap-3" data-testid={`re-marketer-row-${m.id}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold text-sm">{(m.name || "?").slice(0, 2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-black">{m.name}</div>
                <div className="text-xs text-black/55 flex items-center gap-3 mt-0.5 flex-wrap">
                  <span className="font-mono bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{m.referral_code}</span>
                  <span>{m.sales_count} sales</span>
                  <span className="text-emerald-700 font-bold">{ngn(m.wallet_balance)} wallet</span>
                </div>
              </div>
              <button onClick={() => remove(m.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
      {showPromote && <PromoteModal employees={employees} marketers={items} onClose={() => setShowPromote(false)} onPromoted={() => { setShowPromote(false); load(); }} />}
    </div>
  );
}

function PromoteModal({ employees, marketers, onClose, onPromoted }) {
  const [employee_id, setEid] = useState("");
  const [team_lead_id, setTeamLeadId] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [saving, setSaving] = useState(false);
  const eligible = employees.filter((e) => !marketers.find((m) => m.employee_id === e.id));
  const submit = async () => {
    if (!employee_id) return toast.error("Select employee");
    setSaving(true);
    try {
      const r = await api.post("/t/re/marketers/promote", { employee_id, team_lead_id: team_lead_id || null });
      setCredentials(r.data.credentials);
      toast.success("Promoted!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  if (credentials) return (
    <Modal title="Marketer Created" onClose={() => { onPromoted(); }}>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-3">
        <div className="font-bold text-emerald-900 mb-2">Share these login details</div>
        <div className="space-y-1 text-sm">
          <div><span className="text-black/60">Username:</span> <span className="font-mono font-bold">{credentials.username}</span></div>
          <div><span className="text-black/60">Email:</span> <span className="font-mono font-bold">{credentials.email}</span></div>
          <div><span className="text-black/60">Temp password:</span> <span className="font-mono font-bold bg-amber-100 px-2 py-0.5 rounded">{credentials.temporary_password}</span></div>
        </div>
      </div>
      <button onClick={onPromoted} className="w-full px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold">Done</button>
    </Modal>
  );
  return (
    <Modal title="Promote Employee to Marketer" onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Employee *">
          <select value={employee_id} onChange={(e) => setEid(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="promote-employee">
            <option value="">Select…</option>
            {eligible.map((e) => <option key={e.id} value={e.id}>{e.name || e.email}</option>)}
          </select>
        </Field>
        <Field lbl="Team Lead (optional)">
          <select value={team_lead_id} onChange={(e) => setTeamLeadId(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">
            <option value="">None</option>
            {marketers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
          We'll auto-generate a username, secure login password, and a unique referral code.
        </div>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="promote-save" />
    </Modal>
  );
}

function AgenciesView() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => api.get("/t/re/agencies").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="re-agencies-view">
      <div className="flex items-center justify-end mb-4"><button onClick={() => setShow(true)} className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2" data-testid="re-agency-new"><Plus size={14} /> New Agency</button></div>
      {items.length === 0 ? <div className="bg-white rounded-2xl border border-black/8 p-12 text-center"><Briefcase size={32} className="mx-auto text-black/25 mb-3" /><div className="font-bold text-black/70">No agencies yet</div></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-black/8 p-5" data-testid={`re-agency-card-${a.id}`}>
              <div className="font-display font-extrabold text-lg text-black">{a.name}</div>
              <div className="text-xs text-black/55 mt-1">{a.address}</div>
              <div className="flex items-center gap-3 mt-3 text-xs">
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-bold">{a.marketers_count || 0} marketers</span>
                <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-bold">{ngn(a.revenue)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {show && <AgencyEditor onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function AgencyEditor({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", address: "", phone: "", email: "", description: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => { if (!f.name.trim()) return toast.error("Name required"); setSaving(true); try { await api.post("/t/re/agencies", f); toast.success("Created"); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail); } finally { setSaving(false); } };
  return (
    <Modal title="New Agency" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Name *" full><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="agency-name" /></Field>
        <Field lbl="Phone"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></Field>
        <Field lbl="Email"><Input value={f.email} onChange={(v) => setF({ ...f, email: v })} /></Field>
        <Field lbl="Address" full><Input value={f.address} onChange={(v) => setF({ ...f, address: v })} /></Field>
        <Field lbl="Description" full><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
      </div>
      <ModalFooter onCancel={onClose} onSave={submit} saving={saving} testid="agency-save" />
    </Modal>
  );
}

function CommissionsView() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const load = () => { const q = statusF === "all" ? "" : `?status=${statusF}`; api.get(`/t/re/commissions${q}`).then((r) => setItems(r.data || [])); };
  useEffect(() => { load(); }, [statusF]);
  const markPaid = async (id) => { try { await api.post(`/t/re/commissions/${id}/mark-paid`); toast.success("Marked paid"); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  return (
    <div data-testid="re-commissions-view">
      <div className="flex items-center gap-2 mb-4">
        {["all", "pending", "paid"].map((s) => (
          <button key={s} onClick={() => setStatusF(s)} className={`px-3 py-1.5 text-xs font-bold rounded-md ${statusF === s ? "bg-black text-white" : "bg-black/5 text-black/65"}`}>{s.toUpperCase()}</button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {items.length === 0 ? <div className="p-12 text-center"><Wallet size={28} className="mx-auto text-black/25 mb-2" /><div className="font-bold text-black/55">No commissions</div></div> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Sale</Th><Th>Property</Th><Th>Kind</Th><Th right>%</Th><Th right>Amount</Th><Th>Status</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-black/5" data-testid={`re-comm-row-${c.id}`}>
                  <Td className="font-mono text-xs">{c.sale_code}</Td>
                  <Td>{c.property_title}</Td>
                  <Td className="capitalize">{c.kind}</Td>
                  <Td right>{c.pct}%</Td>
                  <Td right className="font-bold text-emerald-700">{ngn(c.amount)}</Td>
                  <Td><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${c.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span></Td>
                  <Td>{c.status === "pending" && <button onClick={() => markPaid(c.id)} className="text-xs font-bold text-emerald-700">Mark paid</button>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PayoutsView() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("pending");
  const load = () => api.get(`/t/re/payouts?status=${statusF}`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, [statusF]);
  const approve = async (id) => { try { await api.post(`/t/re/payouts/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  const reject = async (id) => { const reason = window.prompt("Rejection reason?"); if (reason === null) return; try { await api.post(`/t/re/payouts/${id}/reject`, { reason }); toast.success("Rejected — funds returned to wallet"); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  return (
    <div data-testid="re-payouts-view">
      <div className="flex items-center gap-2 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatusF(s)} data-testid={`re-payouts-filter-${s}`}
            className={`px-3 py-1.5 text-xs font-bold rounded-md ${statusF === s ? "bg-black text-white" : "bg-black/5 text-black/65"}`}>{s.toUpperCase()}</button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {items.length === 0 ? <div className="p-12 text-center"><DollarSign size={28} className="mx-auto text-black/25 mb-2" /><div className="font-bold text-black/55">No {statusF} payouts</div></div> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Marketer</Th><Th>Bank</Th><Th>Account</Th><Th right>Amount</Th><Th>Requested</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-black/5" data-testid={`re-payout-row-${p.id}`}>
                  <Td className="font-bold">{p.marketer_name}</Td>
                  <Td>{p.bank_name}</Td>
                  <Td className="font-mono text-xs">{p.account_number}<div className="text-[11px] text-black/55">{p.account_name}</div></Td>
                  <Td right className="font-bold text-emerald-700">{ngn(p.amount)}</Td>
                  <Td className="text-xs text-black/55">{p.created_at?.slice(0, 10)}</Td>
                  <Td>
                    {p.status === "pending" ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => approve(p.id)} data-testid={`re-payout-approve-${p.id}`} className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded">Approve</button>
                        <button onClick={() => reject(p.id)} data-testid={`re-payout-reject-${p.id}`} className="text-xs font-bold text-red-600 hover:bg-red-50 px-2 py-1 rounded">Reject</button>
                      </div>
                    ) : (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${p.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{p.status}</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SettingsView() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/t/re/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;
  const save = async () => {
    setSaving(true);
    try { await api.patch("/t/re/settings", s); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-6 max-w-2xl" data-testid="re-settings-view">
      <div className="space-y-4">
        <Field lbl="Default closer commission %"><Input type="number" value={s.default_commission_pct} onChange={(v) => setS({ ...s, default_commission_pct: parseFloat(v) || 0 })} testid="set-default-commission" /></Field>
        <Field lbl="Default team-lead override %"><Input type="number" value={s.default_team_lead_pct} onChange={(v) => setS({ ...s, default_team_lead_pct: parseFloat(v) || 0 })} testid="set-default-team-lead" /></Field>
        <Field lbl="Referral commission % (when external code is used)"><Input type="number" value={s.referral_default_pct} onChange={(v) => setS({ ...s, referral_default_pct: parseFloat(v) || 0 })} /></Field>
        <Field lbl="Minimum payout amount (₦)"><Input type="number" value={s.min_payout_amount} onChange={(v) => setS({ ...s, min_payout_amount: parseFloat(v) || 0 })} testid="set-min-payout" /></Field>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!s.require_admin_approval_on_publish} onChange={(e) => setS({ ...s, require_admin_approval_on_publish: e.target.checked })} className="accent-[#1FA868]" />
          Require admin approval before properties go live
        </label>
        <button onClick={save} disabled={saving} className="w-full px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid="re-settings-save">{saving ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
}

// ───────── SHARED UI ─────────
function KPI({ title, value, icon: Icon, accent, testid }) { return <div className="bg-white rounded-2xl border border-black/8 p-4" data-testid={testid}><div className="flex items-start justify-between"><div><div className="text-[11px] uppercase font-bold text-black/55">{title}</div><div className="mt-1 font-display font-extrabold text-2xl text-black">{value}</div></div>{Icon && <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-black/5 text-black/55"}`}><Icon size={18} /></div>}</div></div>; }
function Card({ title, children }) { return <div className="bg-white rounded-2xl border border-black/8 p-5"><div className="font-display font-extrabold text-lg text-black mb-3">{title}</div>{children}</div>; }
function Modal({ title, onClose, children }) { return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}><div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}><div className="px-6 py-4 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white z-10"><div className="font-display font-extrabold text-lg text-black">{title}</div><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button></div><div className="p-6">{children}</div></div></div>; }
function ModalFooter({ onCancel, onSave, saving, testid }) { return <div className="flex gap-2 pt-4 mt-4 border-t border-black/8"><button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button><button onClick={onSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid={testid}>{saving ? "Saving…" : "Save"}</button></div>; }
function Field({ lbl, full, children }) { return <div className={full ? "col-span-2" : ""}><label className="text-[11px] uppercase font-bold text-black/55">{lbl}</label><div className="mt-1">{children}</div></div>; }
function Input({ value, onChange, type = "text", placeholder, testid }) { return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid={testid} />; }
function Th({ children, right }) { return <th className={`px-4 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, className = "" }) { return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>; }
function Spinner() { return <div className="p-12 flex items-center justify-center text-black/45"><Loader2 className="animate-spin" /></div>; }
